import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  SmsRemindersTrackingService,
  SmsReminderRecord,
  SmsRemindersResponse,
  SmsPreviewResponse
} from '../sms-reminders-tracking.service';
import { SmsDetailsDialogComponent } from './sms-details-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../language.service';
import { SettingsService } from '../settings.service';

@Component({
    selector: 'app-sms-reminders-tracking',
    templateUrl: './sms-reminders-tracking.component.html',
    styleUrls: ['./sms-reminders-tracking.component.scss'],
    standalone: false
})
export class SmsRemindersTrackingComponent implements OnInit {

  selectedDate: Date = new Date();
  loading = false;
  error = '';

  smsData: SmsRemindersResponse | null = null;
  filteredRecords: SmsReminderRecord[] = [];

  filterStatus: 'all' | 'success' | 'failed' = 'all';
  filterChanges: 'all' | 'deleted' | 'dateChanged' | 'noChanges' = 'all';
  filterSendType: 'all' | 'cron' | 'manual' = 'all';
  filterArrival: 'all' | 'confirmedUser' | 'confirmedSMS' | 'cancelledSMS' | 'waiting' = 'all';
  filterExamType: number | 'all' = 'all';
  searchText = '';

  constructor(
    private smsService: SmsRemindersTrackingService,
    private dialog: MatDialog,
    private translate: TranslateService,
    public lang: LanguageService,
    private settings: SettingsService
  ) {}

  ngOnInit(): void {
    if (!this.settings.examsSettings?.length) {
      this.settings.getExams();
    }
    this.loadSmsReminders();
  }

  getExamName(examTypeId: number): string {
    if (!examTypeId) return '';
    const exam = this.settings.examsSettings.find((e: any) => e.AutoIndex === examTypeId);
    return exam ? (exam.examName || exam.examType) : examTypeId.toString();
  }

  getUniqueExamTypes(): { id: number; name: string }[] {
    if (!this.smsData) return [];
    const seen = new Set<number>();
    return this.smsData.records
      .filter(r => r.examType && !seen.has(r.examType) && seen.add(r.examType))
      .map(r => ({ id: r.examType, name: this.getExamName(r.examType) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  loadSmsReminders(): void {
    this.loading = true;
    this.error = '';

    const year = this.selectedDate.getFullYear();
    const month = (this.selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = this.selectedDate.getDate().toString().padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    this.smsService.getSmsReminders(dateStr).subscribe({
      next: (data) => {
        this.smsData = data;
        this.calculateCronStats();
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.error = this.translate.instant('appointments.sms-tracking.error.load');
        console.error('Error loading SMS reminders:', err);
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    if (!this.smsData) {
      this.filteredRecords = [];
      return;
    }

    let records = [...this.smsData.records];

    if (this.filterStatus === 'success') {
      records = records.filter(r => r.answerMessage === 'SMS will be sent');
    } else if (this.filterStatus === 'failed') {
      records = records.filter(r => r.answerMessage !== 'SMS will be sent');
    }

    if (this.filterChanges === 'deleted') {
      records = records.filter(r => r.wasDeleted);
    } else if (this.filterChanges === 'dateChanged') {
      records = records.filter(r => r.dateChanged && !r.wasDeleted);
    } else if (this.filterChanges === 'noChanges') {
      records = records.filter(r => !r.wasDeleted && !r.dateChanged);
    }

    if (this.filterSendType === 'cron') {
      records = records.filter(r => this.isCronJobMessage(r));
    } else if (this.filterSendType === 'manual') {
      records = records.filter(r => !this.isCronJobMessage(r));
    }

    if (this.filterArrival === 'confirmedUser') {
      records = records.filter(r => r.arrivalConfirmedViaUser);
    } else if (this.filterArrival === 'confirmedSMS') {
      records = records.filter(r => r.arrivalConfirmedViaSMS && !r.arrivalConfirmedViaUser && r.arrivalConfirm === true);
    } else if (this.filterArrival === 'cancelledSMS') {
      records = records.filter(r => r.arrivalConfirmedViaSMS && !r.arrivalConfirmedViaUser && r.arrivalConfirm === false);
    } else if (this.filterArrival === 'waiting') {
      records = records.filter(r => !r.arrivalConfirmedViaSMS && !r.arrivalConfirmedViaUser);
    }

    if (this.filterExamType !== 'all' && this.filterExamType !== ('all' as any)) {
      records = records.filter(r => r.examType === Number(this.filterExamType));
    }

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      records = records.filter(r =>
        r.First?.toLowerCase().includes(search) ||
        r.Last?.toLowerCase().includes(search) ||
        r.patientID?.toString().includes(search) ||
        r.PID?.toString().includes(search) ||
        r.mobile?.includes(search)
      );
    }

    this.filteredRecords = records;
  }

  onDateChange(dateStr: string): void {
    this.selectedDate = new Date(dateStr);
    this.loadSmsReminders();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  isCronJobMessage(record: SmsReminderRecord): boolean {
    const sentTime = new Date(record.smsSentTime);
    const hours = sentTime.getHours();
    const minutes = sentTime.getMinutes();
    if (hours === 8 && minutes <= 15) return true;
    if (hours === 7 && minutes >= 45) return true;
    return false;
  }

  calculateCronStats(): void {
    if (!this.smsData) return;
    const cronMessages = this.smsData.records.filter(r => this.isCronJobMessage(r));
    const manualMessages = this.smsData.records.filter(r => !this.isCronJobMessage(r));
    (this.smsData.stats as any).cronSent = cronMessages.length;
    (this.smsData.stats as any).manualSent = manualMessages.length;
  }

  getRowClass(record: SmsReminderRecord): string {
    if (record.wasDeleted) return 'row-deleted';
    if (record.dateChanged) return 'row-date-changed';
    if (record.answerMessage !== 'SMS will be sent') return 'row-failed';
    return 'row-success';
  }

  getStatusIcon(record: SmsReminderRecord): string {
    return record.answerMessage === 'SMS will be sent' ? 'check_circle' : 'error';
  }

  getChangesBadge(record: SmsReminderRecord): string {
    if (record.wasDeleted) return this.translate.instant('appointments.sms-tracking.badge.deleted');
    if (record.dateChanged) return this.translate.instant('appointments.sms-tracking.badge.date-changed');
    if (record.totalUserChanges > 0 || record.totalPatientChanges > 0) {
      return `${record.totalUserChanges + record.totalPatientChanges} ${this.translate.instant('appointments.gui.labels.changes')}`;
    }
    return this.translate.instant('appointments.sms-tracking.badge.no-changes');
  }

  getSendTypeBadge(record: SmsReminderRecord): string {
    return this.isCronJobMessage(record)
      ? this.translate.instant('appointments.sms-tracking.badge.auto')
      : this.translate.instant('appointments.sms-tracking.badge.manual');
  }

  getArrivalStatus(record: SmsReminderRecord): string {
    if (record.arrivalConfirmedViaUser) return this.translate.instant('appointments.sms-tracking.arrival.confirmed-user');
    if (record.arrivalConfirmedViaSMS) {
      if (record.arrivalConfirm === true) return this.translate.instant('appointments.sms-tracking.arrival.confirmed-patient');
      if (record.arrivalConfirm === false) return this.translate.instant('appointments.sms-tracking.arrival.declined-patient');
    }
    return this.translate.instant('appointments.sms-tracking.arrival.waiting');
  }

  getArrivalIcon(record: SmsReminderRecord): string {
    if (record.arrivalConfirmedViaUser) return 'check_box';
    if (record.arrivalConfirmedViaSMS) {
      if (record.arrivalConfirm === true) return 'check_circle';
      if (record.arrivalConfirm === false) return 'cancel';
    }
    return 'schedule';
  }

  getArrivalDate(record: SmsReminderRecord): Date | null {
    if (record.arrivalConfirmedViaUser && record.arrivalConfirmedViaUserDate) {
      return record.arrivalConfirmedViaUserDate;
    }
    if (record.arrivalConfirmedViaSMS && record.arrivalConfirmedViaSMSDate) {
      return record.arrivalConfirmedViaSMSDate;
    }
    return null;
  }

  formatDateTime(dateStr: Date | string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  copyLink(confirmationLink: string): void {
    navigator.clipboard.writeText(confirmationLink).catch(err => {
      console.error('Failed to copy link:', err);
      alert(this.translate.instant('appointments.sms-tracking.error.copy-link'));
    });
  }

  copySmsMessage(record: SmsReminderRecord): void {
    if (record.messageBody) {
      navigator.clipboard.writeText(record.messageBody).catch(err => {
        console.error('Failed to copy SMS message:', err);
      });
      return;
    }
    this.smsService.getSmsPreview(record.appointmentId).subscribe({
      next: (smsData: SmsPreviewResponse) => {
        const message = smsData.messageBody || '';
        navigator.clipboard.writeText(message).catch(err => {
          console.error('Failed to copy SMS message:', err);
        });
      },
      error: () => {}
    });
  }

  showDetails(record: SmsReminderRecord): void {
    this.smsService.getSmsPreview(record.appointmentId).subscribe({
      next: (smsData: SmsPreviewResponse) => {
        let arrivalInfo = this.translate.instant('appointments.sms-tracking.arrival.waiting');
        if (record.arrivalConfirmedViaUser && record.arrivalConfirmedViaUserDate) {
          arrivalInfo = `${this.translate.instant('appointments.sms-tracking.arrival.confirmed-user-detail')} - ${this.formatDateTime(record.arrivalConfirmedViaUserDate)}`;
        } else if (record.arrivalConfirmedViaSMS && record.arrivalConfirmedViaSMSDate) {
          if (record.arrivalConfirm === true) {
            arrivalInfo = `${this.translate.instant('appointments.sms-tracking.arrival.confirmed-patient-detail')} - ${this.formatDateTime(record.arrivalConfirmedViaSMSDate)}`;
          } else if (record.arrivalConfirm === false) {
            arrivalInfo = `${this.translate.instant('appointments.sms-tracking.arrival.declined-patient-detail')} - ${this.formatDateTime(record.arrivalConfirmedViaSMSDate)}`;
          }
        }

        const confirmationLink = smsData.confirmationLink || record.confirmationLink || this.translate.instant('appointments.sms-tracking.no-link-found');
        const fullMessage = record.messageBody || smsData.messageBody || '';

        this.dialog.open(SmsDetailsDialogComponent, {
          width: '700px',
          maxWidth: '90vw',
          data: {
            patientName: `${record.First} ${record.Last}`,
            smsSentTime: this.formatDateTime(record.smsSentTime),
            status: record.answerMessage,
            appointmentTime: this.formatDateTime(record.originalAptTime),
            confirmationLink,
            fullMessage,
            arrivalInfo,
            wasDeleted: record.wasDeleted,
            dateChanged: record.dateChanged,
            totalUserChanges: record.totalUserChanges,
            totalPatientChanges: record.totalPatientChanges
          }
        });
      },
      error: () => {
        alert(this.translate.instant('appointments.sms-tracking.error.preview'));
      }
    });
  }

  refresh(): void {
    this.loadSmsReminders();
  }
}
